import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "react-hot-toast";
import { useLanguage } from "../Context/LanguageContext";
import {
  FiMail,
  FiSend,
  FiUser,
  FiMessageSquare,
  FiGithub,
  FiLinkedin,
  FiTwitter,
  FiMapPin,
} from "react-icons/fi";
import { sendContactMessage } from "../services/contactService";
import "./contact.css";

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: { duration: 0.3 },
  },
};

const CodePreview = ({ name, email, message, dateStr }) => (
  <div className="contact-code-preview">
    <div className="code-window__header">
      <span className="dot dot--red" />
      <span className="dot dot--yellow" />
      <span className="dot dot--green" />
      <span className="code-window__title">message.js</span>
    </div>
    <div className="contact-code-preview__body">
      {[
        {
          n: 1,
          content: (
            <>
              <span className="kw">const</span> <span className="fn">button</span>{" "}
              <span className="op">=</span> <span className="fn">document</span>
              <span className="op">.</span>
              <span className="prop">querySelector</span>
              <span className="op">(</span>
              <span className="str">'#sendBtn'</span>
              <span className="op">);</span>
            </>
          ),
        },
        { n: 2, content: <></> },
        {
          n: 3,
          content: (
            <>
              <span className="kw">const</span> <span className="prop">message</span>{" "}
              <span className="op">= {"{"}</span>
            </>
          ),
        },
        {
          n: 4,
          content: (
            <>
              &nbsp;&nbsp;<span className="prop">name:</span>{" "}
              <span className="str">"{name || "..."}"</span>
              <span className="op">,</span>
            </>
          ),
        },
        {
          n: 5,
          content: (
            <>
              &nbsp;&nbsp;<span className="prop">email:</span>{" "}
              <span className="str">"{email || "..."}"</span>
              <span className="op">,</span>
            </>
          ),
        },
        {
          n: 6,
          content: (
            <>
              &nbsp;&nbsp;<span className="prop">message:</span>{" "}
              <span className="str">
                "{message ? message.slice(0, 24) + (message.length > 24 ? "..." : "") : "..."}"
              </span>
              <span className="op">,</span>
            </>
          ),
        },
        {
          n: 7,
          content: (
            <>
              &nbsp;&nbsp;<span className="prop">date:</span>{" "}
              <span className="str">"{dateStr}"</span>
            </>
          ),
        },
        {
          n: 8,
          content: (
            <>
              <span className="op">{"}"}</span>
            </>
          ),
        },
        { n: 9, content: <></> },
        {
          n: 10,
          content: (
            <>
              <span className="fn">button</span>
              <span className="op">.</span>
              <span className="prop">addEventListener</span>
              <span className="op">(</span>
              <span className="str">'click'</span>
              <span className="op">
                , () {"=>"} {"{"}
              </span>
            </>
          ),
        },
        {
          n: 11,
          content: (
            <>
              &nbsp;&nbsp;<span className="fn">form</span>
              <span className="op">.</span>
              <span className="prop">send</span>
              <span className="op">(</span>
              <span className="prop">message</span>
              <span className="op">);</span>
            </>
          ),
        },
        {
          n: 12,
          content: (
            <>
              <span className="op">{"}"});</span>
            </>
          ),
        },
      ].map(({ n, content }) => (
        <div key={n} className="code-line">
          <span className="ln">{n}</span>
          <span className="contact-code-preview__text">{content}</span>
        </div>
      ))}
    </div>
  </div>
);

const ContactMe = () => {
  const { t, language } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const contactLinks = [
    {
      icon: <FiGithub />,
      label: "hebraheem",
      href: "https://github.com/hebraheem",
      color: "#E5E9F0",
    },
    {
      icon: <FiLinkedin />,
      label: "ibrahim-adebiyi",
      href: "https://www.linkedin.com/in/ibrahim-adebiyi-b7b979166/",
      color: "#0077B5",
    },
    {
      icon: <FiTwitter />,
      label: "@just_hebraheem",
      href: "https://twitter.com/just_hebraheem",
      color: "#1DA1F2",
    },
    {
      icon: <FiMail />,
      label: "iadebiyi01@gmail.com",
      href: "mailto:iadebiyi01@gmail.com",
      color: "#43D9AD",
    },
    { icon: <FiMapPin />, label: t("BayernGermany"), href: "#", color: "#E99287" },
  ];

  const date = new Date();
  const dateStr = `${t(date.toLocaleString("en-US", { weekday: "short" }))} ${date.getUTCDate()} ${t(date.toLocaleString("en-US", { month: "short" }))}`;

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const openMailFallback = () => {
    const subject = encodeURIComponent(`Portfolio Contact from ${form.name}`);
    const body = encodeURIComponent(`${form.message}\n\nFrom: ${form.name} <${form.email}>`);
    window.location.href = `mailto:iadebiyi01@gmail.com?subject=${subject}&body=${body}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await sendContactMessage({
        ...form,
        source: "portfolio-web",
        sentAt: new Date().toISOString(),
      });

      setSubmitted(true);
      if (result.delivered) {
        toast.success(t("ContactSuccess"));
      } else {
        toast(t("ContactFallback"), {
          icon: "📬",
        });
        openMailFallback();
      }
    } catch (error) {
      toast.error(t("ContactError"));
      openMailFallback();
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setForm({ name: "", email: "", message: "" });
    setSubmitted(false);
  };
  const isValid = form.name && form.email && form.message;

  return (
    <motion.div
      className="contact-page"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      key={language}
    >
      {/* Left: contact links */}
      <div className="contact-page__sidebar">
        <div className="contact-sidebar__title mono" style={{ color: "var(--text-muted)" }}>
          {`// ${t("FindMeAlsoIn")}`}
        </div>
        <div className="contact-sidebar__links">
          {contactLinks.map(({ icon, label, href, color }) => (
            <motion.a
              key={label}
              href={href}
              target={href !== "#" ? "_blank" : undefined}
              rel="noreferrer"
              className="contact-sidebar__link"
              whileHover={{ x: 6, color }}
              transition={{ duration: 0.15 }}
            >
              <span style={{ color, fontSize: 18 }}>{icon}</span>
              <span className="mono">{label}</span>
            </motion.a>
          ))}
        </div>
      </div>

      {/* Center: Form */}
      <div className="contact-page__form-area">
        <div className="contact-form-wrapper">
          <motion.div
            className="contact-form-header"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="code-window__header">
              <span className="dot dot--red" />
              <span className="dot dot--yellow" />
              <span className="dot dot--green" />
              <span className="code-window__title">{t("Contacts")}</span>
            </div>
          </motion.div>

          {submitted ? (
            <motion.div
              className="contact-success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
            >
              <div className="contact-success__icon">🎉</div>
              <h2 className="contact-success__title">{t("SuccessTitle")}</h2>
              <p className="contact-success__text mono">{t("SuccessText")}</p>
              <motion.button
                className="contact-btn"
                onClick={reset}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
              >
                {t("SendNewMessage")}
              </motion.button>
            </motion.div>
          ) : (
            <form className="contact-form" onSubmit={handleSubmit}>
              <motion.div
                className="contact-form__field"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 }}
              >
                <label className="contact-form__label mono">
                  <FiUser size={13} /> {t("NameLabel")}
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="contact-form__input"
                  placeholder={t("NamePlaceholder")}
                  required
                />
              </motion.div>

              <motion.div
                className="contact-form__field"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.32 }}
              >
                <label className="contact-form__label mono">
                  <FiMail size={13} /> {t("EmailLabel")}
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className="contact-form__input"
                  placeholder={t("EmailPlaceholder")}
                  required
                />
              </motion.div>

              <motion.div
                className="contact-form__field"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.39 }}
              >
                <label className="contact-form__label mono">
                  <FiMessageSquare size={13} /> {t("MessageLabel")}
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  className="contact-form__textarea"
                  rows={6}
                  placeholder={t("MessagePlaceholder")}
                  required
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.46 }}
              >
                {!isValid && (
                  <p className="contact-form__hint mono">{`// ${t("fill all fields to send") || "fill all fields to send"}`}</p>
                )}
                <motion.button
                  type="submit"
                  className={`contact-btn${!isValid ? " contact-btn--disabled" : ""}`}
                  disabled={!isValid || loading}
                  whileHover={isValid ? { scale: 1.04 } : {}}
                  whileTap={isValid ? { scale: 0.97 } : {}}
                >
                  {loading ? (
                    <span className="contact-btn__loading">{t("sending...") || "sending..."}</span>
                  ) : (
                    <>
                      <FiSend size={14} /> {t("SubmitButton")}
                    </>
                  )}
                </motion.button>
              </motion.div>
            </form>
          )}
        </div>
      </div>

      {/* Right: code preview */}
      <div className="contact-page__preview">
        <CodePreview name={form.name} email={form.email} message={form.message} dateStr={dateStr} />
      </div>
    </motion.div>
  );
};

export default ContactMe;
